import { useState, useEffect } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import reactStringReplace from 'react-string-replace'
import { parse } from 'query-string'

import { validateEmail } from 'utils/email-validator'
import { setUserForTracking } from 'utils/tracking/set-user-for-tracking'
import { EVENTS, trackEvent } from 'utils/tracking/events-tracker'
import { trackMonthlyExperiment } from 'utils/monthly-experiment'

import GoogleSignIn from '../../../buttons/GoogleSignIn'
import { TextOnLine } from '../../../../containers/Register/styled'
import Field from '../../../input/InputField'
import { PrimaryButton } from '../../../buttons/Buttons'
import { Text } from '../../../topography'
import { FieldLabel } from '../../../filters/styled'
import CheckBox from '../../../input/CheckBox'
import Title from '../common/Title'
import { Turnstile } from '../../../turnstile'
import {
  getLanguageCode,
  pushWithLanguageRoutes,
  translate,
} from '../../../../utils/translation'
import { getGoogleSignInURL } from '../../../../utils/consts.mjs'
import { fonts, palette } from '../../../../styles/variables'
import { validatePassword } from '../../../../utils/inputValidation'
import { getUser, ipLookUp, login, register } from '../../../../utils/api'
import { isSubUser, signIn } from '../../../../utils/userAuth'
import {
  clearToken,
  removeAIWPhaseOutModalStatus,
  removeAdblockDetectModalStatus,
} from '../../../../utils/storage'
import { selectCurrentUser } from '../../../../state/selectors'
import { selectCachedLocations } from '../../../../state/selectors/locationManager'
import {
  getCountryCode,
  getCountryInfo,
  isCity,
} from '../../../../utils/locations'
import { MIXPANEL_PROPS, Mixpanel } from '../../../../utils/mixpanel'
import { getLocationsInfo } from '../../../../state/actions/locationManager'
import {
  fetchAddons,
  fetchMainUser,
  fetchSubscription,
  fetchUserLimits,
  fetchUserSuccess,
} from '../../../../state/actions/global'
import { fetchProjects } from '../../../../containers/Dashboard/actions'
import { fetchAlerts } from '../../../../state/actions/alerts'
import PasswordTooltip from '../../../input/PasswordTooltip'
import { sendMessage } from '../../../../utils/functions'

export default ({
  defaultType = 'register',
  onAuthSuccess,
  onClose,
  onShowLoginLimitModal,
  turnstileStatus,
  handleTurnstileStatus,
  isSignupModal = false,
  setAuthFormType = null,
  isAddProjectFlow,
  searchType,
  text,
}) => {
  const searchParams = parse(window.location.search)
  const [data, setData] = useState({ email: '', password: '' })
  const [remember, setRemember] = useState(false)
  const [isLoading, setIsLoading] = useState(false)
  const [error, setError] = useState('')
  const [googleError, setGoogleError] = useState(
    searchParams.error === 'existing_email',
  )
  const [type, setType] = useState(defaultType)
  const [isEmailValid, setIsEmailValid] = useState(true)
  const [isPasswordValid, setIsPasswordValid] = useState(true)
  const [showPassword, setShowPassword] = useState(false)
  const [sentPageViewEvent, setSentPageViewEvent] = useState({
    register: false,
    login: false,
  })

  const user = useSelector(selectCurrentUser())
  const cachedLocations = useSelector(selectCachedLocations())
  const dispatch = useDispatch()
  const lang = getLanguageCode()

  useEffect(() => {
    const source =
      isSignupModal && !isAddProjectFlow
        ? MIXPANEL_PROPS.SOURCE.values.ASK_FOR_SIGNUP_MODAL
        : MIXPANEL_PROPS.SOURCE.values.NEW_CHECKOUT_MODAL

    if (type === 'register' && !sentPageViewEvent.register) {
      trackEvent({
        event: EVENTS.SIGN_UP_PAGEVIEW,
        mixpanelProperties: [
          { source, page: MIXPANEL_PROPS.PAGE_NAME.defaultValue() },
        ],
      })
      setSentPageViewEvent({ ...sentPageViewEvent, register: true })
    }
    if (type === 'login' && !sentPageViewEvent.login) {
      trackEvent({
        event: EVENTS.SIGN_IN_PAGEVIEW,
        mixpanelProperties: [
          { source, page: MIXPANEL_PROPS.PAGE_NAME.defaultValue() },
        ],
      })
      setSentPageViewEvent({ ...sentPageViewEvent, login: true })
    }
  }, [isSignupModal, isAddProjectFlow, sentPageViewEvent, type])

  const handleAuthSuccess = async (onSuccess, onFailure) => {
    removeAdblockDetectModalStatus()
    removeAIWPhaseOutModalStatus()

    try {
      const res = await ipLookUp()

      const countryCode = getCountryCode(res)

      const { data: ipData = {} } = res
      const ipLocation = getCountryInfo(countryCode)
      const preferences = ipLocation
        ? {
            lang: ipLocation.languageCode,
            locId: ipLocation.locationId,
          }
        : { lang: 'en', locId: 2840 }
      const userResponse = await getUser()

      // Track trial user session if user is on trial. Used for onboarding experiment
      if (userResponse.data.subscription?.subscriptionStatus === 'trialing') {
        const storageKey = `onboarding_state_${userResponse.data.id}_trial_session_count`
        const rawSessionCount = localStorage.getItem(storageKey)
        const sessionCount = Number(rawSessionCount || '0')
        localStorage.setItem(storageKey, String(sessionCount + 1))
      }

      if (!user || user.id !== userResponse.data.id) {
        setUserForTracking(userResponse.data)
        Mixpanel.setUserProperties({ ...userResponse.data, countryCode })
      }
      const userInfo = {
        ...userResponse.data,
        countryCode,
        country: ipData?.country,
        city: ipData?.city,
        preferences: {
          ...userResponse.data.preferences,
          lang: userResponse.data.preferences?.lang || preferences.lang,
          locId: userResponse.data.preferences?.locId || preferences.locId,
        },
        alerts: undefined,
      }

      const userPreferencesLocId = userResponse.data.preferences?.locId
      if (
        userPreferencesLocId &&
        isCity(userPreferencesLocId) &&
        !cachedLocations[userPreferencesLocId]
      ) {
        dispatch(getLocationsInfo([userPreferencesLocId]))
      }

      signIn(userInfo)

      dispatch(fetchUserSuccess(userInfo))
      dispatch(fetchProjects())
      dispatch(fetchAlerts())
      dispatch(fetchSubscription())
      dispatch(fetchUserLimits())
      dispatch(fetchAddons())
      if (isSubUser(userInfo)) {
        dispatch(fetchMainUser())
      }

      onAuthSuccess?.(userInfo)
      onSuccess?.(userInfo)
      isSignupModal && !isAddProjectFlow && onClose?.()
    } catch (catchError) {
      console.error(catchError)
      setError(translate('unknown_error'))
      onFailure?.()
    }
  }

  const handleSubmit = () => {
    setError('')
    setGoogleError(false)

    setIsEmailValid(validateEmail(data.email))
    setIsPasswordValid(validatePassword(data.password))
    if (!validateEmail(data.email) || !validatePassword(data.password)) return

    setIsLoading(true)
    const { email, password } = data

    const eventId = crypto.randomUUID()

    if (type === 'login') {
      login(email, password, remember, eventId)
        .then((res) => {
          handleAuthSuccess(
            (userInfo) => {
              clearToken()
              const isMonthlyTier1PriceExperimentEnabled =
                trackMonthlyExperiment({
                  user: userInfo,
                })

              trackEvent({
                event: EVENTS.SIGN_IN_SUCCESS,
                mixpanelProperties: [
                  {
                    signInMethod: MIXPANEL_PROPS.SIGN_IN_METHOD.values.EMAIL,
                    source:
                      isSignupModal && !isAddProjectFlow
                        ? MIXPANEL_PROPS.SOURCE.values.ASK_FOR_SIGNUP_MODAL
                        : MIXPANEL_PROPS.SOURCE.values.NEW_CHECKOUT_MODAL,
                    isCheckoutModal: true,
                    isMonthlyTier1PriceExperimentEnabled,
                  },
                ],
                gtmProperties: {
                  userData: {
                    email: userInfo.email,
                    externalId: userInfo.id,
                  },
                  eventId,
                },
              })
              if (!res.data.fresh_login) {
                onShowLoginLimitModal?.()
              }
              setIsLoading(false)
            },
            () => {
              setIsLoading(false)
            },
          )
        })
        .catch((err) => {
          if (
            err.response &&
            err.response.data.description.error === 'user_not_confirmed'
          ) {
            sendMessage('SHOW_EMAIL_CONFIRM_MODAL', {
              email,
            })
            setError(translate('user_not_confirmed'))
          } else {
            setError(
              translate(
                err.response
                  ? err.response.data.description.error
                  : 'unknown_error',
              ),
            )
          }
          setIsLoading(false)
        })
    } else {
      register(
        email.trim(),
        password,
        lang,
        null,
        user?.country,
        user?.city,
        eventId,
      )
        .then(() => {
          handleAuthSuccess(
            (userInfo) => {
              trackEvent({
                event: EVENTS.SIGN_UP_SUCCESS,
                mixpanelProperties: [
                  {
                    signInMethod: MIXPANEL_PROPS.SIGN_IN_METHOD.values.EMAIL,
                    source:
                      isSignupModal && !isAddProjectFlow
                        ? MIXPANEL_PROPS.SOURCE.values.ASK_FOR_SIGNUP_MODAL
                        : MIXPANEL_PROPS.SOURCE.values.NEW_CHECKOUT_MODAL,
                    isCheckoutModal: true,
                  },
                ],
                gtmProperties: {
                  userData: {
                    email: userInfo.email,
                    externalId: userInfo.id,
                  },
                  eventId,
                },
              })
              setIsLoading(false)
            },
            () => {
              setIsLoading(false)
            },
          )
        })
        .catch((err) => {
          setError(
            translate(
              err.response
                ? err.response.data.description.error
                : 'unknown_error',
            ),
          )
          setIsLoading(false)
        })
    }
  }

  const getButtonTag = () => {
    if (isSignupModal && !isAddProjectFlow && type === 'register') {
      return translate('get_started_free')
    }

    if (['login', 'register'].includes(type)) {
      return translate(type)
    }

    return translate('continue')
  }

  const getGoogleRedirectUrl = () => {
    const eventId = crypto.randomUUID()

    if (isAddProjectFlow) {
      const newSearchParams = new URLSearchParams({
        checkout: true,
        action: 'add_project_checkout',
        titleUpgradePlan: searchType,
        eventId,
        ...(text && { reachedText: text }),
      })

      return getGoogleSignInURL({
        event_id: eventId,
        next:
          window.location.pathname +
          (window.location.search
            ? `${window.location.search}&${newSearchParams}`
            : `?${newSearchParams}`),
      })
    }

    if (isSignupModal) {
      const newSearchParams = new URLSearchParams({
        checkout: true,
        signup: true,
        eventId,
      })

      const search = window.location.search
        ? `${window.location.search}&${newSearchParams}`
        : `?${newSearchParams}`

      return getGoogleSignInURL({
        event_id: eventId,
        next: `${window.location.pathname}${search}`,
      })
    }

    const newSearchParams = new URLSearchParams({
      checkout: true,
      eventId,
    })

    const search = window.location.search
      ? `${window.location.search}&${newSearchParams}`
      : `?${newSearchParams}`

    return getGoogleSignInURL({
      event_id: eventId,
      next: `${window.location.pathname}${search}`,
    })
  }

  return (
    <>
      {!isSignupModal && (
        <Title
          dangerouslySetInnerHTML={{ __html: translate('try_ubersuggest') }}
        />
      )}
      <GoogleSignIn
        theme="dark"
        label={translate('continue_with_google')}
        disabled={!turnstileStatus?.isVerified}
        redirectURL={getGoogleRedirectUrl()}
        width="100%"
        height="48px"
        minWidth="fit-content"
        mobileStretch
        style={{
          marginTop: '24px',
          marginBottom: '10px',
        }}
        labelStyle={{
          fontSize: '12px',
          lineHeight: '12px',
          letterSpacing: '0.08em',
          fontFamily: fonts.primaryMedium,
          width: '100%',
          textAlign: 'center',
          textTransform: 'uppercase',
        }}
        onClick={() => {
          if (!turnstileStatus?.isVerified) return
          setGoogleError(false)
        }}
      />
      {googleError && (
        <Text
          fontFamily={fonts.primaryLight}
          fontSize="13px"
          margin="10px 0 15px 0"
          color={palette.danger}
          padding="0 30px"
          textAlign="center"
        >
          {translate('registered_google_email')}
        </Text>
      )}
      <TextOnLine
        margin="20px 0"
        textStyle={{
          color: palette.darkGray50,
          textAlign: 'center',
          whiteSpace: 'nowrap',
          position: 'relative',
          transform: 'none',
          left: 'none',
          top: 'none',
          marginTop: '1px',
        }}
        text={translate('continue_with_email')}
        style={{
          width: '100%',
          maxWidth: '100%',
          display: 'flex',
          justifyContent: 'center',
          height: 'fit-content',
          lineHeight: 1,
        }}
      />
      <form
        onSubmit={(e) => {
          e.preventDefault()
          handleSubmit()
        }}
      >
        <FieldLabel style={{ height: 'auto' }}>{translate('email')}</FieldLabel>
        <Field
          placeholder={translate('enter_email')}
          name="email"
          value={data.email}
          onChange={(e) => {
            setData({ ...data, email: e.target.value })
            setIsEmailValid(true)
          }}
          isValid={isEmailValid}
          invalidMessage={translate('invalid_email')}
          styles={{ width: '100%' }}
          tabIndex={0}
          style={{
            '::placeholder': {
              color: palette.darkGray50,
              fontFamily: fonts.primaryBook,
            },
          }}
        />
        <div style={{ marginTop: '6px' }}>
          <FieldLabel style={{ height: 'auto' }}>
            {translate('password')}
          </FieldLabel>
          <Field
            placeholder={
              type === 'login'
                ? translate('enter_password')
                : translate('create_password')
            }
            name="password"
            type={showPassword ? 'text' : 'password'}
            value={data.password}
            onChange={(e) => {
              setData({ ...data, password: e.target.value })
              setIsPasswordValid(true)
            }}
            isValid={isPasswordValid}
            invalidMessage={translate('invalid_password')}
            styles={{ width: '100%' }}
            tabIndex={0}
            style={{
              '::placeholder': {
                color: palette.darkGray50,
                fontFamily: fonts.primaryBook,
              },
            }}
            rightElement={() => (
              <PasswordTooltip
                show={showPassword}
                toggle={() => setShowPassword(!showPassword)}
                style={{ position: 'absolute', right: 0, top: '-6px' }}
              />
            )}
          />
        </div>
        {!!error && (
          <Text
            fontFamily={fonts.primaryLight}
            fontSize="13px"
            margin="0px 0 15px 0"
            color={palette.danger}
            dangerouslySetInnerHTML={{ __html: error }}
          />
        )}
        {type === 'login' && (
          <div
            style={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'flex-end',
              margin: '4px 0 10px 0',
              flexDirection: 'row',
            }}
          >
            <CheckBox
              checked={remember}
              onChange={() => {
                setRemember(!remember)
              }}
              style={{ alignItems: 'flex-end' }}
            >
              <Text
                fontSize={lang === 'ja' ? '12px' : '14px'}
                lineHeight="14px"
              >
                {translate('remember_login')}
              </Text>
            </CheckBox>
            <Text
              onClick={() => {
                onClose?.()
                pushWithLanguageRoutes(`/forgot-password?email=${data.email}`)
              }}
              style={{
                marginLeft: '5px',
                fontSize: lang === 'ja' ? '12px' : '14px',
                lineHeight: '16px',
                color: palette.orange,
                textDecoration: 'underline',
                cursor: 'pointer',
                display: 'inline-block',
              }}
            >
              {translate('login_forgot_password')}
            </Text>
          </div>
        )}
        <Turnstile
          style={{ marginTop: '15px' }}
          turnstileStatus={turnstileStatus}
          handleTurnstileStatus={handleTurnstileStatus}
        />
        <PrimaryButton
          type="submit"
          tag={getButtonTag()}
          theme="orange"
          width="100%"
          fontSize="14px"
          letterSpacing="0.08em"
          style={{
            lineHeight: 1,
            marginTop: '14px',
          }}
          disabled={
            !data.email ||
            !data.password ||
            isLoading ||
            !turnstileStatus?.isVerified
          }
          isLoading={isLoading}
        />
      </form>
      {type === 'register' && (
        <Text
          fontSize="10px"
          lineHeight="16px"
          color={palette.darkGray50}
          fontFamily={fonts.primary}
          margin="16px 0 0 0"
          linkColor={palette.darkGray50}
          boldFontFamily={fonts.primary}
          dangerouslySetInnerHTML={{
            __html: `${reactStringReplace(
              reactStringReplace(
                translate(
                  'terms_agreement',
                  translate('terms_of_service'),
                  translate('privacy_policy'),
                ),
                translate('privacy_policy'),
                (match) => {
                  return `<a href="https://neilpatel.com/privacy/" target="_blank">${match}</a>`
                },
              ),
              translate('terms_of_service'),
              (match) => {
                return `<a href="https://neilpatel.com/terms/" target="_blank">${match}</a>`
              },
            ).join('')}`,
          }}
        />
      )}
      {type === 'register' && (
        <Text
          fontSize="14px"
          lineHeight="18px"
          fontFamily={fonts.primary}
          margin="16px 0 0 0"
          textAlign="center"
        >
          {translate('already_have_account')}
          <Text
            onClick={() => {
              if (!isLoading) {
                setError('')
                setGoogleError(false)
                setType('login')
                setAuthFormType && setAuthFormType('login')
                setData({ ...data, password: '' })
              }
            }}
            style={{
              marginLeft: '5px',
              fontSize: '14px',
              lineHeight: '18px',
              color: palette.orange,
              textDecoration: 'underline',
              display: 'inline-block',
              cursor: 'pointer',
            }}
          >
            {translate('login_button')}
          </Text>
        </Text>
      )}
      {type === 'login' && (
        <Text
          fontFamily={fonts.primary}
          fontSize="14px"
          lineHeight="18px"
          margin="24px 0 0"
          textAlign="center"
        >
          {reactStringReplace(
            translate('no_account', translate('register_here')),
            translate('register_here'),
            (match, i) => (
              <Text
                key={`title-${i}`}
                onClick={() => {
                  if (!isLoading) {
                    setError('')
                    setGoogleError(false)
                    setType('register')
                    setAuthFormType && setAuthFormType('signup')
                    setData({ ...data, password: '' })
                  }
                }}
                style={{
                  marginLeft: '5px',
                  fontSize: '14px',
                  lineHeight: '18px',
                  color: palette.orange,
                  textDecoration: 'underline',
                  cursor: 'pointer',
                  display: 'inline-block',
                }}
              >
                {match}
              </Text>
            ),
          )}
        </Text>
      )}
    </>
  )
}
